import React from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import classNames from 'classnames/bind';

import Heading from 'components/shared/heading';
import Link from 'components/shared/link';

import shape1 from './images/shape-1.svg';
import shape2 from './images/shape-2.svg';
import dotPattern from './images/dot-pattern.svg';

import styles from './complete-system.module.scss';

const cx = classNames.bind(styles);

const CompleteSystem = ({ className: classNameAdditional = '', title, description, items }) => {
  const { videoIntercom, mobileApp, accessControl, hardware } = useStaticQuery(graphql`
    query {
      videoIntercom: file(relativePath: { eq: "shared/complete-system/video-intercom.png" }) {
        childImageSharp {
          gatsbyImageData(width: 330)
        }
      }
      mobileApp: file(relativePath: { eq: "shared/complete-system/mobile-app.png" }) {
        childImageSharp {
          gatsbyImageData(width: 311)
        }
      }
      accessControl: file(relativePath: { eq: "shared/complete-system/access-control.png" }) {
        childImageSharp {
          gatsbyImageData(width: 585)
        }
      }
      hardware: file(relativePath: { eq: "shared/complete-system/hardware.png" }) {
        childImageSharp {
          gatsbyImageData(width: 121)
        }
      }
    }
  `);

  const imageCollection = {
    videoIntercom,
    mobileApp,
    accessControl,
    hardware,
  };

  return (
    <section className={cx('wrapper', classNameAdditional)}>
      <div className={cx('container', 'inner')}>
        <Heading className={cx('title')} tag="h2" size="xl" innerHTML={title} />
        <p className={cx('description')}>{description}</p>

        <div className={cx('items-wrapper')}>
          <div className={cx('items-inner')}>
            {items.map(({ title, link, url, imageName }, index) => {
              const image = imageCollection[imageName];
              return (
                <Link className={cx('item', imageName)} to={link?.url || url} key={index}>
                  <Heading className={cx('item-subtitle')} tag="h3" size="lg" innerHTML={title} />
                  <GatsbyImage className={cx('item-image')} image={getImage(image)} alt="" />
                </Link>
              );
            })}
          </div>
        </div>

        <img
          className={cx('shape-1')}
          src={shape1}
          alt=""
          loading="lazy"
          aria-hidden
          width="591"
          height="439"
        />
        <img
          className={cx('dot-pattern-1')}
          src={dotPattern}
          alt=""
          loading="lazy"
          aria-hidden
          width="145"
          height="145"
        />

        <img
          className={cx('shape-2')}
          src={shape2}
          alt=""
          loading="lazy"
          aria-hidden
          width="474"
          height="393"
        />
        <img
          className={cx('dot-pattern-2')}
          src={dotPattern}
          alt=""
          loading="lazy"
          aria-hidden
          width="145"
          height="145"
        />
      </div>
    </section>
  );
};

CompleteSystem.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      link: PropTypes.shape({
        url: PropTypes.string,
      }),
      imageName: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default CompleteSystem;
